import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { payoutCreator } from "@/lib/stripe.functions";
import { listPendingReferrals, markReferralPaid } from "@/lib/referrals.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/payouts")({
  head: () => ({ meta: [{ title: "Payouts — Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminPayouts,
});

function AdminPayouts() {
  const qc = useQueryClient();
  const pay = useServerFn(payoutCreator);
  const listRefs = useServerFn(listPendingReferrals);
  const markPaid = useServerFn(markReferralPaid);

  const pending = useQuery({
    queryKey: ["admin-payouts-pending"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("commissions")
        .select("creator_id, commission_amount, currency, creators(name, handle)")
        .eq("status", "approved");
      if (error) return [];
      const groups = new Map<string, { creator_id: string; name: string; handle: string; total: number; count: number; currency: string }>();
      for (const c of data as any[]) {
        const k = c.creator_id;
        const cur = groups.get(k) ?? { creator_id: k, name: c.creators?.name ?? "Unknown", handle: c.creators?.handle ?? "", total: 0, count: 0, currency: c.currency ?? "USD" };
        cur.total += Number(c.commission_amount ?? 0);
        cur.count += 1;
        groups.set(k, cur);
      }
      return Array.from(groups.values()).sort((a, b) => b.total - a.total);
    },
  });

  const recent = useQuery({
    queryKey: ["admin-payouts-recent"],
    queryFn: async () => {
      const { data } = await supabase.from("payouts").select("*, creators(name)").order("created_at", { ascending: false }).limit(20);
      return data ?? [];
    },
  });

  const mut = useMutation({
    mutationFn: (creatorId: string) => pay({ data: { creatorId } }),
    onSuccess: (res, creatorId) => {
      if (!res.ok) return toast.error(res.error ?? "Payout failed");
      toast.success(`Paid out ${((res.amount ?? 0) / 100).toFixed(2)}`);
      qc.invalidateQueries({ queryKey: ["admin-payouts-pending"] });
      qc.invalidateQueries({ queryKey: ["admin-payouts-recent"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Payout failed"),
  });

  const referrals = useQuery({
    queryKey: ["admin-pending-referrals"],
    queryFn: () => listRefs(),
  });

  const stripeAccounts = useQuery({
    queryKey: ["admin-stripe-accounts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("stripe_accounts")
        .select("creator_id, stripe_account_id, charges_enabled, payouts_enabled, details_submitted, creators(name, handle)")
        .order("creator_id");
      return data ?? [];
    },
  });

  const markRefMut = useMutation({
    mutationFn: (referralId: string) => markPaid({ data: { referralId } }),
    onSuccess: () => {
      toast.success("Referral bonus marked paid");
      qc.invalidateQueries({ queryKey: ["admin-pending-referrals"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  return (
    <div className="max-w-5xl space-y-8 sm:space-y-10">
      <div>
        <Link to="/admin" className="text-sm text-muted-foreground">← Admin</Link>
        <h1 className="mt-2 text-2xl sm:text-3xl font-semibold">Payouts</h1>
        <p className="text-sm sm:text-base text-muted-foreground">Send Stripe transfers to creators with approved commissions.</p>
      </div>

      <section>
        <h2 className="text-lg font-semibold mb-3">Creator Stripe accounts</h2>
        <div className="overflow-x-auto border border-border">
          {(stripeAccounts.data ?? []).length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No creators have connected Stripe yet.</p>
          ) : (
            <table className="w-full min-w-[480px] text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Creator</th>
                  <th className="px-4 py-3 text-left">Stripe Account ID</th>
                  <th className="px-4 py-3 text-left">Onboarded</th>
                  <th className="px-4 py-3 text-left">Payouts enabled</th>
                </tr>
              </thead>
              <tbody>
                {stripeAccounts.data!.map((a: any) => (
                  <tr key={a.creator_id} className="border-t border-border">
                    <td className="px-4 py-3">{a.creators?.name} <span className="text-muted-foreground">@{a.creators?.handle}</span></td>
                    <td className="px-4 py-3"><code className="text-xs">{a.stripe_account_id}</code></td>
                    <td className="px-4 py-3">
                      <span className={a.details_submitted ? "text-green-600" : "text-amber-600"}>
                        {a.details_submitted ? "Yes" : "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={a.payouts_enabled ? "text-green-600" : "text-amber-600"}>
                        {a.payouts_enabled ? "Enabled" : "Not yet"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Pending</h2>
        <div className="overflow-x-auto border border-border">
          {(pending.data ?? []).length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No approved commissions waiting for payout.</p>
          ) : (
            <table className="w-full min-w-[480px] text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3 text-left">Creator</th><th className="px-4 py-3 text-left">Commissions</th><th className="px-4 py-3 text-left">Total</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {pending.data!.map((g) => (
                  <tr key={g.creator_id} className="border-t border-border">
                    <td className="px-4 py-3">{g.name} <span className="text-muted-foreground">@{g.handle}</span></td>
                    <td className="px-4 py-3">{g.count}</td>
                    <td className="px-4 py-3">${g.total.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => mut.mutate(g.creator_id)} disabled={mut.isPending} className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-50">
                        {mut.isPending ? "Sending…" : "Pay out"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Recent payouts</h2>
        <div className="overflow-x-auto border border-border">
          {(recent.data ?? []).length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No payouts yet.</p>
          ) : (
            <table className="w-full min-w-[480px] text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Creator</th><th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Status</th></tr>
              </thead>
              <tbody>
                {recent.data!.map((p: any) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-4 py-3">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">{p.creators?.name ?? "—"}</td>
                    <td className="px-4 py-3">${(p.amount_cents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Pending referral bonuses</h2>
        <div className="overflow-x-auto border border-border">
          {(referrals.data?.rows ?? []).length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No pending referral bonuses.</p>
          ) : (
            <table className="w-full min-w-[600px] text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Referrer</th>
                  <th className="px-4 py-3 text-left">Referred creator</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Bonus</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {referrals.data!.rows.map((r: any) => {
                  const referrer = Array.isArray(r.referrer) ? r.referrer[0] : r.referrer;
                  const referred = Array.isArray(r.referred) ? r.referred[0] : r.referred;
                  return (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-4 py-3">{referrer?.name} <span className="text-muted-foreground">@{referrer?.handle}</span></td>
                      <td className="px-4 py-3">{referred?.name} <span className="text-muted-foreground">@{referred?.handle}</span></td>
                      <td className="px-4 py-3">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 tabular-nums">£{Number(r.bonus_gbp).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => markRefMut.mutate(r.id)}
                          disabled={markRefMut.isPending}
                          className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background disabled:opacity-50"
                        >
                          Mark paid
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}